import { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import {
	EMPTY_FLEET_FILTERS,
	GET_FLEET_COMPONENTS_DEFAULT_QUERY_PARAMS
} from '../../../../constants/hardware-inventory';
import { toFleetFilters } from '../../../../helpers/hardwareInventory';
import { analyseSmartSearch } from './hardwareInventoryMapper';

const FILTER_DEBOUNCE_MS = 400;

/*
 * Owns the filter bar, the search box and the drill-down for the fleet inventory tab: the draft the
 * inputs render, the committed query the fetch effect watches, and the debouncing between them.
 *
 * `componentTypes` is the analytics catalog the search box resolves a typed phrase against.
 * `onDrillDown` is called when an analytics row is selected, so the page can bring the fleet tab
 * forward - the hook decides that a drill-down happened, and deliberately owns no tab state itself.
 */
export const useFleetInventoryFilters = (componentTypes, onDrillDown) => {
	/*
	 * `filters` is the draft the inputs render, so typing stays responsive; `queryParams` is what has
	 * actually been committed and fetched. Keeping them apart is what lets the text fields debounce
	 * without the caret lagging behind the keystrokes.
	 */
	const [filters, setFilters] = useState(EMPTY_FLEET_FILTERS);
	const [smartQuery, setSmartQuery] = useState('');
	const [queryParams, setQueryParams] = useState(GET_FLEET_COMPONENTS_DEFAULT_QUERY_PARAMS);
	const [selectedComponentType, setSelectedComponentType] = useState(null);

	/*
	 * Any filter change returns to the first page: a narrower query can match fewer rows than the
	 * current offset, which would otherwise land on an empty page.
	 */
	const commitFilters = (nextFilters) => {
		setQueryParams((currentParams) => {
			return { ...currentParams, ...nextFilters, start: 0 };
		});
	};

	/*
	 * The debounced function has to keep one identity for its timer to survive re-renders, but a
	 * function captured once would also capture that first render's scope forever. Calling through a
	 * ref that is repointed every render keeps the identity stable while always running the current
	 * implementation, so `commitFilters` stays free to read component state later without silently
	 * going stale.
	 */
	const commitFiltersRef = useRef(commitFilters);

	commitFiltersRef.current = commitFilters;

	const debouncedCommitFilters = useMemo(() => {
		return _.debounce((nextFilters) => {
			return commitFiltersRef.current(nextFilters);
		}, FILTER_DEBOUNCE_MS);
	}, []);

	useEffect(() => {
		return () => {
			debouncedCommitFilters.cancel();
		};
	}, [debouncedCommitFilters]);

	const commitFiltersNow = (nextFilters) => {
		debouncedCommitFilters.cancel();
		commitFilters(nextFilters);
	};

	/*
	 * Editing a field by hand invalidates the drill-down chip: the query is no longer that component
	 * type's, so the chip would be claiming a filter that is not applied any more.
	 */
	const onFilterChange = (field, value) => {
		const nextFilters = { ...filters, [field]: value };

		setFilters(nextFilters);
		setSelectedComponentType(null);

		if (field === 'category') {
			commitFiltersNow(nextFilters);

			return;
		}

		debouncedCommitFilters(nextFilters);
	};

	/*
	 * The box resolves one phrase into the (manufacturer, model) pair the API can filter on, and
	 * writes it into those two fields so the result is visible rather than hidden. Category,
	 * firmware and serial are left alone, so the box narrows within whatever else is set.
	 */
	const onSmartQueryChange = (value) => {
		const nextFilters = { ...filters, ...analyseSmartSearch(value, componentTypes).filters };

		setSmartQuery(value);
		setFilters(nextFilters);
		setSelectedComponentType(null);
		debouncedCommitFilters(nextFilters);
	};

	/*
	 * Recomputed from the committed query rather than stored, so the hint can never drift out of
	 * step with the filters actually in force.
	 */
	const smartSearch = useMemo(() => {
		return analyseSmartSearch(smartQuery, componentTypes);
	}, [smartQuery, componentTypes]);

	const onClearAll = () => {
		debouncedCommitFilters.cancel();
		setFilters(EMPTY_FLEET_FILTERS);
		setSmartQuery('');
		setSelectedComponentType(null);
		setQueryParams(GET_FLEET_COMPONENTS_DEFAULT_QUERY_PARAMS);
	};

	const onPaginationChange = (start, limit) => {
		setQueryParams((currentParams) => {
			return { ...currentParams, start, limit };
		});
	};

	/*
	 * Drilling down from the firmware tab starts a fresh query rather than narrowing the current
	 * one: the previous type's filters and any serial search would otherwise AND with the new type
	 * server-side and return nothing. Switching tabs is what makes the split still answer "which
	 * appliances are on the old version?" in one gesture.
	 */
	const onSelectComponentType = (componentType) => {
		const nextFilters = toFleetFilters(componentType);

		debouncedCommitFilters.cancel();
		setSelectedComponentType(componentType);
		setFilters(nextFilters);
		setSmartQuery('');
		setQueryParams({ ...GET_FLEET_COMPONENTS_DEFAULT_QUERY_PARAMS, ...nextFilters });
		onDrillDown?.();
	};

	return {
		filters,
		smartQuery,
		smartSearch,
		queryParams,
		selectedComponentType,
		onFilterChange,
		onSmartQueryChange,
		onClearAll,
		onPaginationChange,
		onSelectComponentType
	};
};
